import { createSlice, createSelector, createEntityAdapter, createAsyncThunk } from "@reduxjs/toolkit";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
import { simpleSetPayload, PREVIEW_BOARD_ID, objectSetPayload } from "../constants";
import { VIDEO_SESSION_PAGES as VSP } from "@app/common/data/ActivitiessEnums";
import { SLICE_NAMES } from "./sliceNames";
import { callApi } from "@amplio-common/api";
import { SELF_PRACTICE_STATUSES } from "@app/routes/treatment/constants";
import { toast } from "react-toastify";
import { Trans } from "@lingui/macro";
import moment from "moment";

//define root selector
const CONTENT_SLICE_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.CONTENT_SLICE];
const sortFunc = sortByFieldAlphabetically("name");

const createSimpleSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.CONTENT_SLICE][fieldName]], field => field,
);

export const studentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

export const setPinnedBoard = createAsyncThunk(
  `${SLICE_NAMES.CONTENT_SLICE}/setPinnedBoard`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { newBoardId, src } = payload;

    if (!src) {
      // eslint-disable-next-line no-console
      console.warn("setPinnedBoard - misuse. missing 'src' attribute for analytics. proper usage is 'setPinnedBoard({ newBoardId: X, src:\"change-reason\"");
    }

    if (!newBoardId && payload) {
      // eslint-disable-next-line no-console
      console.warn("setPinnedBoard - misuse. payload should be object with newBoardId and src (analytics). proper usage is 'setPinnedBoard({ newBoardId: X, src:\"change-reason\"");
    }

    const boardIdToUse = newBoardId || payload; // support misuse of setPinnedBoard.

    // eslint-disable-next-line no-use-before-define
    boardIdToUse !== PREVIEW_BOARD_ID && dispatch(setIsPresenting(false));
   
    return boardIdToUse;
  },
);


export const mapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.name,
  firstName: ts?.student?.first_name,
  boardMode: null,
  isLocked: false,
});
  
const studentsAdapterSelectors = studentsAdapter.getSelectors(CONTENT_SLICE_STATE_ROOT_SELECTOR);

const initialState = {
  previewBoardMode: null,
  pinnedBoard: PREVIEW_BOARD_ID,
  isPresenting: false,
  isShowAssignInfoPopper: false,
  isShareScreenOn: false,
  isShareScreenRequested: false,
  isShowPresentShareDiag: false,
  pinnedBoardFromScreenShare: PREVIEW_BOARD_ID,
  isShowActivityStudentList: false,
  lastVisitedLibrary: VSP.VOCABULARY,
  assignInfoStudentId: null,
  isInstructionsOpen: false,
  instructionsPopupCoords: { x: null, y: null },
  isGameCustomFilterOpen: false,
  customFilterStudentList: [],
  isActivityPreviewPressed: false,
  lastPinnedBoard: PREVIEW_BOARD_ID,
  isGameRefreshed: false,
  isSelfPracticeDialogOpen: false,
  isArticulationPracticeGame: false, // State for adding articulation game
  inSession: false,
  selfPracticeForm: {
    name: "",
    instructions: "",
    game_list: [],
    treatment_id: "",
  },
  ...studentsAdapter.getInitialState(),
};


export const treatmentContentSlice = createSlice({
  name: SLICE_NAMES.CONTENT_SLICE,
  initialState,
  reducers: {
    clearContentSlice: (state) => ({ ...initialState, previewBoardMode: state.previewBoardMode, lastVisitedLibrary: state.lastVisitedLibrary }),
    // setPinnedBoard: (state, action) => { state.pinnedBoard = action.payload.newBoardId; },
    // setPreviewBoardMode: simpleSetPayload("previewBoardMode"),
    setPreviewBoardMode: (state, action) => {
      state.previewBoardMode = action.payload;

      if ([VSP.VOCABULARY, VSP.ACTIVITY].includes(action.payload)) {
        state.lastVisitedLibrary = action.payload;
      }
    },
    setIsPresenting: simpleSetPayload("isPresenting"),
    setIsShowAssignInfoPopper: simpleSetPayload("isShowAssignInfoPopper"),
    setIsShareScreenOn: simpleSetPayload("isShareScreenOn"),
    setAssignInfoStudentId: simpleSetPayload("assignInfoStudentId"),
    setCustomFilterStudentList: simpleSetPayload("customFilterStudentList"),
    setIsActivityPreviewPressed: simpleSetPayload("isActivityPreviewPressed"),
    setLastPinnedBoard: simpleSetPayload("lastPinnedBoard"),
    setIsGameRefreshed: simpleSetPayload("isGameRefreshed"),
    setIsSelfPracticeDialogOpen: simpleSetPayload("isSelfPracticeDialogOpen"),
    setSelfPracticeFormData: objectSetPayload("selfPracticeForm"),
    clearSelfPracticeFormData: (state, action) => {
      state.selfPracticeForm.instructions = "";
      state.selfPracticeForm.name = action.payload.name;
      state.selfPracticeForm.treatment_id = action.payload.treatment_id || action.payload.treatment.treatment_id;
      state.selfPracticeForm.student_id = action.payload.student_id;
      if (!action.payload.status) {
        state.selfPracticeForm.status = SELF_PRACTICE_STATUSES.NOT_SET.id;
      }
      // if (state.selfPracticeForm.game_list[0]?.treatment_id === state.selfPracticeForm.treatment_id) return;
      state.selfPracticeForm.game_list = [];
    },
    pushGameToList: (state, action) => {
      const { newGame, students } = action.payload;
      if (!state.isSelfPracticeDialogOpen) {
        try {
          const practice_name = `תרגול עצמי לטיפול ${moment(action.payload.treatment?.start_date).format("DD MMMM")}`;
          callApi(
            "self_practice/add_game/",
            { method: "POST",
              body: {
                game: newGame,
                students: JSON.stringify(students),
                treatment_id: newGame.treatment_id,
                practice_name,
              },
            },
          );
        } catch (err) {
          toast.error(<Trans>Failed to save game</Trans>);
        }
      }
      if (state.selfPracticeForm.game_list.find(game => game.id === newGame.id)) return;
      state.selfPracticeForm.game_list.push(newGame);
    },
    removeGameFromList: (state, action) => {
      state.selfPracticeForm.game_list = state.selfPracticeForm.game_list.filter(game => game !== action.payload);
    },
    setSelfPracticeGameList: (state, action) => {
      state.selfPracticeForm.game_list = action.payload;
    },
    
    setIsShareScreenRequested: (state, action) => {
      if (state.pinnedBoard !== PREVIEW_BOARD_ID && action.payload) {
        state.isShowPresentShareDiag = true;
        return;
      }
      state.isShareScreenRequested = action.payload;
    },
    //this functions resets screenshare and activates pinned board to the id saved in "pinnedBoardFromScreenShare"
    setPinnedBoardByForce: (state) => {
      state.pinnedBoard = state.pinnedBoardFromScreenShare;
      state.isShowPresentShareDiag = false;
      state.isShareScreenOn = false;
      state.lastPinnedBoard = state.pinnedBoardFromScreenShare;
    },
    setIsShareScreenRequestedByForce: (state) => {
      state.pinnedBoard = PREVIEW_BOARD_ID;
      state.lastPinnedBoard = PREVIEW_BOARD_ID;
      state.isShowPresentShareDiag = false;
      state.isShareScreenRequested = true;
    },
    setIsShowPresentShareDiag: simpleSetPayload("isShowPresentShareDiag"),
    setIsShowActivityStudentList: simpleSetPayload("isShowActivityStudentList"),
    setLastVisitedLibrary: simpleSetPayload("lastVisitedLibrary"),
    setIsInstructionsOpen: simpleSetPayload("isInstructionsOpen"),
    setInstructionsPopupCoords: simpleSetPayload("instructionsPopupCoords"),
    setIsGameCustomFilterOpen: simpleSetPayload("isGameCustomFilterOpen"),
    setIsArticulationPractice: simpleSetPayload("isArticulationPracticeGame"),
    setInSession: simpleSetPayload("inSession"),

    contentUpdateStudentStatus: studentsAdapter.updateOne,
    contentSetStudentsStatus: studentsAdapter.setAll,
    contentUpdateAllStudents: studentsAdapter.updateMany,
  },
  extraReducers: {
    // [setPinnedBoard.fulfilled]: simpleSetPayload("pinnedBoard"),
    [setPinnedBoard.fulfilled]: (state, action) => {
      if (state.isShareScreenOn && action.payload !== PREVIEW_BOARD_ID) {
        state.isShowPresentShareDiag = true;
        state.pinnedBoardFromScreenShare = action.payload;
      } else {
        state.pinnedBoard = action.payload;
        // state.lastPinnedBoard = action.payload;
      }
    },
  },
});

export default treatmentContentSlice.reducer;

//actions
export const { clearContentSlice, contentUpdateStudentStatus,
  contentSetStudentsStatus, contentUpdateAllStudents, setPreviewBoardMode,
  setIsPresenting, setIsShareScreenOn, setIsShareScreenRequested, setIsShowPresentShareDiag, setLastVisitedLibrary,
  setPinnedBoardByForce, setIsShareScreenRequestedByForce, setIsShowActivityStudentList, setIsShowAssignInfoPopper,
  setAssignInfoStudentId, setIsInstructionsOpen, setInstructionsPopupCoords,
  setIsGameCustomFilterOpen, setIsArticulationPractice, setCustomFilterStudentList, setIsActivityPreviewPressed, setLastPinnedBoard,
  setIsGameRefreshed, setIsSelfPracticeDialogOpen, setSelfPracticeFormData, pushGameToList, removeGameFromList, editDescription,
  setSelfPracticeGameList, clearSelfPracticeFormData, setInSession } = treatmentContentSlice.actions;

//selectors
export const { selectAll: selectAllStudentsStatus, selectIds: selectStudentStatusIds, selectById: selectStudentStatusById } = studentsAdapterSelectors;


export const getPreviewBoardMode = state => state.treatmentContentSlice.previewBoardMode;
export const getPinnedBoard = state => state.treatmentContentSlice.pinnedBoard;

export const selectPinnedBoard = createSimpleSelector("pinnedBoard");
export const selectPreviewBoardMode = createSimpleSelector("previewBoardMode");
export const selectIsPresenting = createSimpleSelector("isPresenting");
export const selectIsShowAssignInfoPopper = createSimpleSelector("isShowAssignInfoPopper");
export const selectIsShareScreenOn = createSimpleSelector("isShareScreenOn");
export const selectIsShowPresentShareDiag = createSimpleSelector("isShowPresentShareDiag");
export const selectIsShareScreenRequested = createSimpleSelector("isShareScreenRequested");
export const selectAssignInfoStudentId = createSimpleSelector("assignInfoStudentId");
export const selectIsPreview = createSelector([getPinnedBoard], pinnedBoard => pinnedBoard === PREVIEW_BOARD_ID);
export const selectIsShowActivityStudentList = createSimpleSelector("isShowActivityStudentList");
export const selectLastVisitedLibrary = createSimpleSelector("lastVisitedLibrary");
export const selectIsInstructionsOpen = createSimpleSelector("isInstructionsOpen");
export const selectInstructionsPopupCoords = createSimpleSelector("instructionsPopupCoords");
export const selectIsGameCustomFilterOpen = createSimpleSelector("isGameCustomFilterOpen");
export const selectIsArticulationPractice = createSimpleSelector("isArticulationPracticeGame");
export const selectCustomFilterStudentList = createSimpleSelector("customFilterStudentList");
export const selectIsActivityPreviewPressed = createSimpleSelector("isActivityPreviewPressed");
export const selectLastPinnedBoard = createSimpleSelector("lastPinnedBoard");
export const selectIsGameRefreshed = createSimpleSelector("isGameRefreshed");
export const selectIsSelfPracticeDialogOpen = createSimpleSelector("isSelfPracticeDialogOpen");
export const selectSelfPracticeFormData = createSimpleSelector("selfPracticeForm");
export const selectInSession = createSimpleSelector("inSession");

export const selectPinnedBoardMode = createSelector(
  [getPinnedBoard, getPreviewBoardMode, studentsAdapterSelectors.selectEntities],
  (pinnedBoard, previewBoardMode, studentsStatus) => pinnedBoard === PREVIEW_BOARD_ID ? previewBoardMode : studentsStatus[pinnedBoard]?.boardMode,
);

export const { selectEntities: selectAllStudentContentStatusDict } = studentsAdapterSelectors;

export const selectStudentBoardModeById = createSelector(
  [selectStudentStatusById], studentStatus => studentStatus?.boardMode,
);
